import clsx from 'clsx';
import React from 'react';
import { IconArrowDown, IconArrowUp, IconArrowUpDown } from './icons';

/**
 * Data table.
 * - 40px rows (`sm` 36 / `lg` 48), 12px horizontal cell padding, hairline
 *   row dividers.
 * - Header is 36px: an uppercase micro-label on a faint sunken band, so the
 *   column names read as structure, not data. Sortable headers are buttons.
 * - Wide tables scroll inside `v2-scroll-x`, which shades the edge that has
 *   more columns behind it.
 * - Every row takes a faint accent tint on hover; rows with `onActivate` are
 *   also keyboard stops (Enter/Space open the record) and the open record's
 *   row is marked `aria-current`.
 * - No border of its own. The table runs edge to edge inside a `Surface`
 *   (list pages) or the workspace pane (master/detail).
 */

export const Table: React.FC<
  React.TableHTMLAttributes<HTMLTableElement> & { fixed?: boolean }
> = ({ className, fixed, ...rest }) => (
  <table
    className={clsx(
      'w-full border-collapse text-v2-13',
      // Outer cells pick up the page gutter so content aligns with the header.
      '[&_tr>*:first-child]:pl-4 lg:[&_tr>*:first-child]:pl-6 [&_tr>*:last-child]:pr-4 lg:[&_tr>*:last-child]:pr-6',
      fixed && 'table-fixed',
      className,
    )}
    {...rest}
  />
);

export const THead: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = (
  props,
) => (
  <thead
    className="sticky top-0 z-[1] bg-v2-surface [&_th]:border-b [&_th]:border-v2-line [&_th]:bg-v2-sunken/50"
    {...props}
  />
);

export const TBody: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = (
  props,
) => <tbody {...props} />;

export type SortDir = 'asc' | 'desc';

export const TH: React.FC<
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    align?: 'left' | 'right' | 'center';
    sortable?: boolean;
    active?: boolean;
    dir?: SortDir;
    onSort?: () => void;
    width?: number | string;
  }
> = ({
  align = 'left',
  sortable,
  active,
  dir,
  onSort,
  className,
  children,
  width,
  style,
  ...rest
}) => {
  const inner = sortable ? (
    <button
      type="button"
      onClick={onSort}
      className={clsx(
        'group -mx-1 inline-flex h-6 items-center gap-1 rounded-v2-sm px-1 uppercase hover:text-v2-fg',
        align === 'right' && 'flex-row-reverse',
        active && 'text-v2-fg',
      )}
      aria-label={
        typeof children === 'string' ? `Sort by ${children}` : undefined
      }
    >
      <span>{children}</span>
      <span
        className={clsx(
          'text-v2-fg-4 transition-opacity',
          active ? 'text-v2-fg-2' : 'opacity-0 group-hover:opacity-100',
        )}
      >
        {active ? (
          dir === 'asc' ? (
            <IconArrowUp size={12} />
          ) : (
            <IconArrowDown size={12} />
          )
        ) : (
          <IconArrowUpDown size={12} />
        )}
      </span>
    </button>
  ) : (
    children
  );
  return (
    <th
      scope="col"
      aria-sort={
        active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined
      }
      style={{ width, ...style }}
      className={clsx(
        'h-9 whitespace-nowrap px-3 text-v2-11 font-semibold uppercase tracking-[0.06em] text-v2-fg-3',
        align === 'left' && 'text-left',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
      {...rest}
    >
      {inner}
    </th>
  );
};

const NESTED_CONTROL =
  'a[href],button,input,select,textarea,label,[role="button"],[role="menuitem"],[role="menuitemradio"],[role="link"]';

/** True when the event came from a real control inside the row. */
const fromNestedControl = (e: React.SyntheticEvent<HTMLElement>) => {
  const control = (e.target as HTMLElement).closest(NESTED_CONTROL);
  return !!control && control !== e.currentTarget;
};

/**
 * Row height. `md` (40px) is the default for every list. `sm` (36px) is for
 * dense tools and admin tables where more rows on screen matter more than
 * air. `lg` (48px) is for lists whose unit of reading is a person rather
 * than a record — a guest with a booking — where the name, a chip and a
 * pill share the row. Pair with the same `size` on the row's cells.
 */
export type RowSize = 'sm' | 'md' | 'lg';
const rowHeight: Record<RowSize, string> = {
  sm: 'h-9',
  md: 'h-10',
  lg: 'h-12',
};

export const TR: React.FC<
  Omit<React.HTMLAttributes<HTMLTableRowElement>, 'onClick'> & {
    /** Marks the row whose record is open beside the list. */
    selected?: boolean;
    /**
     * Makes the row a keyboard stop that opens its record on click, Enter or
     * Space. Clicks and keys on nested links/buttons are left to them.
     */
    onActivate?: () => void;
    unread?: boolean;
    size?: RowSize;
  }
> = ({
  selected,
  onActivate,
  unread,
  size = 'md',
  className,
  onKeyDown,
  ...rest
}) => (
  <tr
    aria-current={selected ? 'true' : undefined}
    tabIndex={onActivate ? 0 : undefined}
    onClick={
      onActivate
        ? (e) => {
            if (!fromNestedControl(e)) onActivate();
          }
        : undefined
    }
    onKeyDown={(e) => {
      onKeyDown?.(e);
      if (
        !onActivate ||
        e.defaultPrevented ||
        e.target !== e.currentTarget ||
        (e.key !== 'Enter' && e.key !== ' ')
      ) {
        return;
      }
      e.preventDefault();
      onActivate();
    }}
    className={clsx(
      'group border-b border-v2-line/80 transition-colors last:border-b-0',
      rowHeight[size],
      // Faint accent tint on hover: a reading aid in dense tables, and the
      // one place the accent appears at rest-adjacent strength.
      !selected && 'hover:bg-v2-accent-soft',
      onActivate && 'cursor-pointer',
      selected && 'bg-v2-accent/[0.07] hover:bg-v2-accent/[0.09]',
      unread && '[&>td:first-child]:relative',
      className,
    )}
    {...rest}
  />
);

export const TD: React.FC<
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    align?: 'left' | 'right' | 'center';
    muted?: boolean;
    num?: boolean;
    truncate?: boolean;
    size?: RowSize;
  }
> = ({
  align = 'left',
  muted,
  num,
  truncate,
  size = 'md',
  className,
  ...rest
}) => (
  <td
    className={clsx(
      'px-3 align-middle',
      rowHeight[size],
      align === 'left' && 'text-left',
      align === 'right' && 'text-right',
      align === 'center' && 'text-center',
      muted && 'text-v2-fg-3',
      num && 'v2-num',
      truncate && 'max-w-0 truncate',
      className,
    )}
    {...rest}
  />
);

/**
 * Group header inside a grouped list (pipeline columns as rows). 32px, sunken,
 * sticky under the table header. Label · count · optional right-hand total.
 * A grouped list is the V2 answer to a kanban: same table, no drag and drop.
 */
export const TRGroup: React.FC<{
  colSpan: number;
  label: React.ReactNode;
  count?: number;
  total?: React.ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
}> = ({ colSpan, label, count, total, collapsed, onToggle }) => (
  <tr className="sticky top-9 z-[1] h-8 border-b border-v2-line bg-v2-sunken/80 backdrop-blur-sm">
    <td colSpan={colSpan} className="h-8 px-3">
      <div className="flex items-center gap-2">
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            className="-ml-1 inline-flex h-6 items-center gap-1.5 rounded-v2-sm px-1 text-v2-12 font-semibold text-v2-fg hover:bg-v2-line/60"
          >
            <span
              aria-hidden
              className={clsx(
                'inline-block text-v2-fg-3 transition-transform',
                collapsed && '-rotate-90',
              )}
            >
              <IconArrowDown size={12} />
            </span>
            {label}
          </button>
        ) : (
          <span className="text-v2-12 font-semibold text-v2-fg">{label}</span>
        )}
        {typeof count === 'number' && (
          <span className="v2-num text-v2-12 text-v2-fg-3">{count}</span>
        )}
        {total != null && (
          <span className="v2-num ml-auto text-v2-12 text-v2-fg-3">
            {total}
          </span>
        )}
      </div>
    </td>
  </tr>
);

/** Blue hairline at the row's left edge for unread/new rows. */
export const UnreadMarker: React.FC = () => (
  <span
    aria-hidden
    className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-sm bg-v2-accent"
  />
);

/**
 * Placeholder rows. `cols` is a count, or a list of per-column class names
 * so responsive `hidden xl:table-cell` columns stay aligned with the header.
 */
export const TableSkeletonRows: React.FC<{
  rows?: number;
  cols: number | (string | undefined)[];
  size?: RowSize;
}> = ({ rows = 8, cols, size = 'md' }) => {
  const list =
    typeof cols === 'number'
      ? Array.from({ length: cols }, () => undefined)
      : cols;
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr
          key={r}
          className={clsx('border-b border-v2-line/80', rowHeight[size])}
        >
          {list.map((cls, c) => (
            <td key={c} className={clsx('px-3', cls)}>
              <span
                className="v2-skeleton block h-3"
                style={{ width: `${45 + ((r * 7 + c * 13) % 45)}%` }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

export const Pagination: React.FC<{
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPage: (p: number) => void;
  noun: string;
}> = ({ page, totalPages, totalCount, pageSize, onPage, noun }) => {
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);
  return (
    <div className="flex h-10 items-center justify-between border-t border-v2-line px-4 text-v2-12 text-v2-fg-3 lg:px-6">
      <span className="v2-num">
        {from}–{to} of {totalCount.toLocaleString()} {noun}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="h-7 rounded-v2 px-2 font-medium text-v2-fg-2 hover:bg-v2-sunken disabled:opacity-40 disabled:hover:bg-transparent"
        >
          Previous
        </button>
        <span className="v2-num px-1">
          {page} / {Math.max(totalPages, 1)}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          className="h-7 rounded-v2 px-2 font-medium text-v2-fg-2 hover:bg-v2-sunken disabled:opacity-40 disabled:hover:bg-transparent"
        >
          Next
        </button>
      </div>
    </div>
  );
};
